import {Plus, Trash2} from 'lucide-react';
import {MirrorLock} from '../mirrorLock';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {TextArea, RichTextEligibleField, Field, type SectionProps} from '../controls';
import type {ConfirmationNextStep, ConfirmationFaqItem} from '@common/enrollmentSite/types';

export function ConfirmationSection({config, patch}: SectionProps) {
  const confirmation = config.confirmation;
  const steps = confirmation.whatHappensNext ?? [];
  const faq = confirmation.faq ?? [];

  function patchConfirmation(next: Partial<typeof confirmation>) {
    patch({confirmation: {...confirmation, ...next}});
  }
  function updateStep(i: number, next: Partial<ConfirmationNextStep>) {
    patchConfirmation({whatHappensNext: steps.map((s, idx) => (idx === i ? {...s, ...next} : s))});
  }
  function updateFaq(i: number, next: Partial<ConfirmationFaqItem>) {
    patchConfirmation({faq: faq.map((f, idx) => (idx === i ? {...f, ...next} : f))});
  }

  return (
    <div className='space-y-5'>
      <MirrorLock field='effectiveDateNote'>
        <TextArea
          label='Effective-date note'
          hint='Shown when any plan is elected. Falls back to a generic HR line.'
          value={confirmation.effectiveDateNote}
          onChange={(effectiveDateNote) => patchConfirmation({effectiveDateNote: effectiveDateNote || undefined})}
        />
      </MirrorLock>

      <MirrorLock field='confirmationCopy'>
        <Field label='What happens next' hint='Numbered next-steps on the receipt.'>
          <div className='space-y-3'>
            {steps.map((step, i) => (
              <div key={i} className='space-y-2 rounded-lg border p-3'>
                <div className='flex items-center justify-between'>
                  <span className='text-xs font-medium text-muted-foreground'>Step {i + 1}</span>
                  <Button
                    variant='ghost'
                    size='icon'
                    aria-label='Remove step'
                    onClick={() => patchConfirmation({whatHappensNext: steps.filter((_, idx) => idx !== i)})}
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
                <Input value={step.title} placeholder='Title' onChange={(e) => updateStep(i, {title: e.target.value})} />
                <RichTextEligibleField label='Body' value={step.body} onChange={(body) => updateStep(i, {body})} />
              </div>
            ))}
            <Button
              variant='outline'
              size='sm'
              onClick={() => patchConfirmation({whatHappensNext: [...steps, {title: '', body: ''}]})}
            >
              <Plus className='mr-1.5 h-3.5 w-3.5' />
              Add step
            </Button>
          </div>
        </Field>

        <Field label='Receipt FAQ' hint='"Common questions" accordion on the receipt.'>
          <div className='space-y-3'>
            {faq.map((item, i) => (
              <div key={i} className='space-y-2 rounded-lg border p-3'>
                <div className='flex items-center justify-between'>
                  <span className='text-xs font-medium text-muted-foreground'>Question {i + 1}</span>
                  <Button
                    variant='ghost'
                    size='icon'
                    aria-label='Remove question'
                    onClick={() => patchConfirmation({faq: faq.filter((_, idx) => idx !== i)})}
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
                <Input value={item.question} placeholder='Question' onChange={(e) => updateFaq(i, {question: e.target.value})} />
                <RichTextEligibleField label='Answer' value={item.answer} onChange={(answer) => updateFaq(i, {answer})} />
              </div>
            ))}
            <Button variant='outline' size='sm' onClick={() => patchConfirmation({faq: [...faq, {question: '', answer: ''}]})}>
              <Plus className='mr-1.5 h-3.5 w-3.5' />
              Add question
            </Button>
          </div>
        </Field>
      </MirrorLock>
    </div>
  );
}
